import { useState, useRef } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search, Music2, Plus, X } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"

type PrimaryImage = { url: string; alt_text: string }

type BandRow = {
  id: number
  name: string
  slug: string
  members_count: number
  recordings_count: number
  gigs_count: number
  in_amh: boolean
  primary_image: PrimaryImage | null
}

type Props = {
  bands: BandRow[]
  total_count: number
  filtered_count: number | null
  search: string | null
  can_create: boolean
}

function BandCard({ band }: { band: BandRow }) {
  return (
    <Link
      href={`/bands/${band.slug}`}
      className="group block w-full text-left bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150 no-underline"
    >
      <div className="aspect-square bg-stone-100 dark:bg-stone-800 relative overflow-hidden">
        {band.primary_image ? (
          <img
            src={band.primary_image.url}
            alt={band.primary_image.alt_text}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music2 className="w-16 h-16 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
          </div>
        )}
        {band.in_amh && (
          <span className="absolute top-2 left-2 text-xs font-medium text-amber-700 dark:text-amber-300 bg-white/90 dark:bg-stone-900/90 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded-full leading-none">
            AMH
          </span>
        )}
      </div>

      <div className="p-3">
        <h3
          className="text-sm font-semibold text-stone-900 dark:text-white mb-1.5 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {band.name}
        </h3>
        <div
          className="flex items-center text-xs text-stone-400 dark:text-stone-500"
          style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
        >
          <span>{band.members_count}m</span>
          <span className="mx-1.5 text-stone-300 dark:text-stone-700">·</span>
          <span>{band.recordings_count}r</span>
          <span className="mx-1.5 text-stone-300 dark:text-stone-700">·</span>
          <span>{band.gigs_count}g</span>
        </div>
      </div>
    </Link>
  )
}

function statusText(bands: BandRow[], total_count: number, filtered_count: number | null, search: string | null): string {
  if (search) {
    const count = filtered_count ?? bands.length
    if (bands.length < count) return `Showing 100 of ${count.toLocaleString()} bands matching "${search}"`
    return `${count.toLocaleString()} band${count !== 1 ? "s" : ""} matching "${search}"`
  }
  return `Search to browse all ${total_count.toLocaleString()} Bands`
}

export default function BandsIndex({ bands, total_count, filtered_count, search, can_create }: Props) {
  const [query, setQuery] = useState(search ?? "")
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleQueryChange(value: string) {
    setQuery(value)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      router.get(
        "/bands",
        value.trim() ? { search: value.trim() } : {},
        { preserveState: true, preserveScroll: true }
      )
    }, 300)
  }

  function clearSearch() {
    setQuery("")
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    router.get("/bands", {}, { preserveState: true, preserveScroll: true })
  }

  const status = statusText(bands, total_count, filtered_count, search ?? null)

  return (
    <>
      <Head title="Bands">
        <meta name="description" content="Browse all Australian bands in the Australian Music Database." />
        <meta property="og:title" content="Bands" />
        <meta property="og:description" content="Browse all Australian bands in the Australian Music Database." />
      </Head>

      <PublicShell>
        {/* Page header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-3xl font-bold text-stone-900 dark:text-white mb-1 tracking-tight"
              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
            >
              Bands
            </h1>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {status}
            </p>
          </div>
          {can_create && (
            <Link
              href="/bands/new"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors no-underline shrink-0"
            >
              <Plus className="w-4 h-4" />
              New band
            </Link>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-8 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
          <input
            type="search"
            placeholder="Search bands…"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            className="w-full pl-9 pr-9 py-2.5 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg text-sm text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-400 dark:focus:border-amber-500 transition-colors"
          />
          {query && (
            <button
              onClick={clearSearch}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Results */}
        {bands.length === 0 ? (
          <div className="py-20 text-center">
            <Music2 className="w-7 h-7 text-stone-300 dark:text-stone-700 mx-auto mb-3" />
            <p className="text-sm text-stone-400 dark:text-stone-500">
              No bands found for &ldquo;{query}&rdquo;
            </p>
            <button
              onClick={clearSearch}
              className="mt-3 text-sm text-amber-600 dark:text-amber-400 hover:underline"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}>
            {bands.map((band) => (
              <BandCard key={band.id} band={band} />
            ))}
          </div>
        )}
      </PublicShell>
    </>
  )
}
